// Notification types and per-user settings (SCREENS → Notifications, DATA_MODEL users).
import { z } from 'zod';

export const NOTIFICATION_TYPES = [
	'application_accepted',
	'application_rejected',
	'event_updated',
	'event_cancelled',
	'chat_message',
	'new_participant',
	'new_application',
	'event_reminder',
	'review_request',
	/** Neutral notice after a moderation action (PRIVACY §5.3): event hidden, account banned. */
	'moderation_notice',
] as const;

export type NotificationType = (typeof NOTIFICATION_TYPES)[number];

/**
 * `users.notification_settings`. Missing keys mean "on". Unknown or malformed content is
 * treated as defaults, so a bad value never blocks delivery logic.
 */
export const notificationSettingsSchema = z
	.object({
		/** Per-type bot message toggles; `false` turns a type off (the in-app row is still kept). */
		types: z.partialRecord(z.enum(NOTIFICATION_TYPES), z.boolean()).default({}),
		/** Quiet hours 23:00–08:00 in the city's timezone (SCREENS). */
		quietHours: z.boolean().default(true),
	})
	.catch({ types: {}, quietHours: true });

export type NotificationSettings = z.infer<typeof notificationSettingsSchema>;

/** Types a user may switch off (S15). Moderation notices always go out (PRIVACY §5.3). */
export const TOGGLEABLE_NOTIFICATION_TYPES = NOTIFICATION_TYPES.filter(
	(type): type is Exclude<NotificationType, 'moderation_notice'> => type !== 'moderation_notice',
);
export type ToggleableNotificationType = (typeof TOGGLEABLE_NOTIFICATION_TYPES)[number];

/** `GET`/`PUT /v1/me/notification-settings` (T-903): every toggleable type, resolved. */
export const notificationSettingsViewSchema = z.object({
	types: z.record(z.enum(TOGGLEABLE_NOTIFICATION_TYPES), z.boolean()),
	quietHours: z.boolean(),
});

export const updateNotificationSettingsRequestSchema = z.strictObject({
	types: z.partialRecord(z.enum(TOGGLEABLE_NOTIFICATION_TYPES), z.boolean()).optional(),
	quietHours: z.boolean().optional(),
});

/** `GET /v1/me/notifications` (S15 center): newest first, the same text the bot sends. */
export const notificationsQuerySchema = z.object({
	cursor: z.string().max(200).optional(),
	limit: z.coerce.number().int().min(1).max(50).default(20),
});

export const notificationsResponseSchema = z.object({
	items: z.array(
		z.object({
			id: z.uuid(),
			type: z.enum(NOTIFICATION_TYPES),
			text: z.string(),
			/** A `startapp` payload (`event_<id>`, …): parse with `parseStartParam`; null: no link. */
			link: z.string().nullable(),
			read: z.boolean(),
			createdAt: z.iso.datetime(),
		}),
	),
	nextCursor: z.string().nullable(),
	unreadCount: z.number().int(),
});

/** `POST /v1/me/notifications/read`: marks everything up to now as read. */
export const markNotificationsReadResponseSchema = z.object({ unreadCount: z.number().int() });

export type NotificationSettingsView = z.infer<typeof notificationSettingsViewSchema>;
export type UpdateNotificationSettingsRequest = z.infer<
	typeof updateNotificationSettingsRequestSchema
>;
export type NotificationsQuery = z.infer<typeof notificationsQuerySchema>;
export type NotificationsResponse = z.infer<typeof notificationsResponseSchema>;
